import Cookies from 'js-cookie';
import jwtDecode from 'jwt-decode';
import jwtEncode from 'jwt-encode';

import partylogo from '../assets/images/apc-logo.png';
import companylogo from '../assets/images/ielect-mark.png';
import { notifyTokenChanged } from '../authz/token-events';
import type { IInputSearchResult } from '../components/reusable/input-search/utils';
import type { IUseAppModal } from '../hooks/modal';
import type { IStates } from '../interface/IReducer';
import type { ILogin, IUserState } from '../interface/state/IAuthState';
import type { IGetPartyByIDState } from '../interface/state/IPartyState';

// Was a cookie; now a localStorage key. The constant name is kept so existing
// imports keep working — the value still acts as a single storage key, just
// in localStorage instead. Migration reason: the JWT envelope produced by
// encodeData runs ~7.5 KB for super-admin accounts (~70 action claims in the
// backend JWT), which exceeds the browser's ~4 KB per-cookie limit. Cookies.set
// was silently failing and admins couldn't log in. localStorage has no such
// limit at this scale (multi-MB per origin).
export const USERDATACOOKIE = '_ud1234567891EL0';

/** localStorage keys with this prefix survive `onLogout`. Never store a credential under it. */
export const KEEP_ACROSS_LOGOUT_PREFIX = 'ielect:keep:';

export const onLogout = () => {
	// Legacy cookie cleanup — harmless once removed but kept in case a session
	// from before the localStorage migration still has it set.
	Cookies.remove(USERDATACOOKIE);
	// Everything goes except `ielect:keep:*` — per-user acknowledgements (the team member's
	// "Your access" card) that must outlive a sign-out. Nothing under that prefix is a credential.
	const kept: [string, string][] = [];
	try {
		for (let i = 0; i < localStorage.length; i += 1) {
			const key = localStorage.key(i);
			if (key?.startsWith(KEEP_ACROSS_LOGOUT_PREFIX)) kept.push([key, localStorage.getItem(key) ?? '']);
		}
	} catch {
		// Storage blocked: nothing to keep.
	}
	localStorage.clear();
	kept.forEach(([key, value]) => {
		try {
			localStorage.setItem(key, value);
		} catch {
			// Best effort.
		}
	});
	notifyTokenChanged();
};

export interface IToken {
	UserId: string;
	FullName: string;
	Email: string;
	role: string;
	nbf: number;
	exp: number;
	iat: number;
	iss: string;
	aud: string;
}

export interface IResponseToken {
	token: string;
	refreshToken: string;
	sub: string;
	name: string;
	iat: number;
}

export const encodeData = (token: string, refreshToken: string) => {
	try {
		const jst = jwtEncode(
			{
				sub: '1516239022',
				name: 'ERMS',
				iat: Math.floor(Date.now() / 1000), // Use a valid timestamp
				token,
				refreshToken,
			} as IResponseToken,
			process.env.REACT_APP_COOKIESECRET
		);
		localStorage.setItem(USERDATACOOKIE, jst);
		// The token carries the user's `action` claims, so every permission gate must
		// re-read it — session extension rewrites this value mid-session.
		notifyTokenChanged();
	} catch (error) {
		console.error(error);
	}
};

export const decodeData = <T>(dataArg: string) => {
	try {
		if (!dataArg) throw new Error('data not valid');
		const data: T = jwtDecode(dataArg);
		if (!data) throw new Error('user not logged in');
		return data;
	} catch (e) {
		return {} as T;
	}
};

const getUser = (): IResponseToken => {
	try {
		const data = localStorage.getItem(USERDATACOOKIE) ?? '';

		return decodeData(data);
	} catch (e) {
		return {} as IResponseToken;
	}
};

const getRoleInArray = (userRole: string[] | string | undefined) =>
	userRole ? (typeof userRole === 'string' ? [userRole] : userRole) : [];

export const getUserData = (): ILogin => {
	try {
		const userData = getUser();

		if (!userData || !('token' in userData) || !('refreshToken' in userData)) throw new Error('invalid token and refreh token');

		const token = userData.token;
		const refreshToken = userData.refreshToken;

		if (!token || !refreshToken) throw new Error('invalid TOken and refreh token');

		const userDecodedData = decodeData<IUserState>(token);

		return {
			user: { ...userDecodedData, role: getRoleInArray(userDecodedData.role) },
			token,
			refreshToken,
		};
	} catch (e) {
		return {} as ILogin;
	}
};

// TODO: Replace with var(--color-primary) from _tokens.scss
// Migration targets: src/components/reusable/svgs/index.tsx (line 589)
export const PRIMARY_COLOR = '#286439';
// TODO: Replace with var(--color-primary-light) from _tokens.scss
// Migration targets: src/components/reusable/hooks.ts (line 53)
export const PRIMARY_COLOR_LIGHT = 'rgb(218, 239, 224)';

export const ISSESSION = () => {
	const timestamp = getUserData()?.user?.exp || 0;
	if (!timestamp) return false;
	const currentTimestamp = Math.floor(Date.now() / 1000);
	return timestamp > currentTimestamp;
	// return true
};

export const ISADMINLOGGED = () => !!getUserData().user?.UserId;
// export const ISADMINLOGGED = () => true;

export const COREAPI = process.env.REACT_APP_BASE_CORE?.replace(/\/+$/, '');
export const COMMANDAPI = process.env.REACT_APP_BASE_COMMAND?.replace(/\/+$/, '');
export const QUERYAPI = process.env.REACT_APP_BASE_QUERY?.replace(/\/+$/, '');
export const EXTRACTAPI = process.env.REACT_APP_BASE_EXTRACT?.replace(/\/+$/, '');
export const DATAAPI = process.env.REACT_APP_BASE_DATA?.replace(/\/+$/, '');
export const VESRAPI = process.env.REACT_APP_BASE_VESR?.replace(/\/+$/, '');
export const FEEDBACKAPI = process.env.REACT_APP_BASE_FEEDBACK?.replace(/\/+$/, '');
/**
 * api.bot through the gateway (`/bot/**`) — the Service Registry admin surface and the base the
 * connection kit hands to partner services. May be undefined: the page says so instead of failing.
 */
export const BOTAPI = process.env.REACT_APP_BASE_BOT?.replace(/\/+$/, '');
/**
 * External media byte store for the Media Gallery (eTraffica). Deliberately an env var rather than
 * a hardcoded host like WALLETAPI below: it is the one thing that has to be swappable, because the
 * store holds only bytes while api.core holds the metadata of record.
 *
 * May be undefined — a build without it must disable uploads and say so, not fail on submit.
 */
export const MEDIAAPI = process.env.REACT_APP_BASE_MEDIA?.replace(/\/+$/, '');
/**
 * api.lms — the learning service behind iElect Academy (`ielect.lms.app`). Reached on its own host:
 * the gateway does not route `/lms` yet (it answers 502), so this is the service origin with no
 * `/api/v1/lms` suffix.
 *
 * May be undefined — a build without it must say the service isn't configured, not fail on fetch.
 */
export const LMSAPI = process.env.REACT_APP_BASE_LMS?.replace(/\/+$/, '');
/**
 * api.notification's WhatsApp surface — capabilities, templates, consent, delivery detail.
 *
 * May be undefined. A build without it must report WhatsApp as *unresolved* rather than unavailable:
 * "we cannot tell whether the channel is ready" and "the channel is not ready" are different claims,
 * and only the second justifies telling an operator their message cannot be sent.
 *
 * Note this is a read surface only. The send endpoint on the same service is gated on a service API
 * key, not a user JWT, and a browser must never hold that secret — broadcasts go through
 * POST /orchestration/OrchestrationJobs.
 */
export const NOTIFICATIONAPI = process.env.REACT_APP_BASE_NOTIFICATION?.replace(/\/+$/, '');
/**
 * Public Mapbox token (`pk.…`) for the polling-unit map and the details-page thumbnail. Baked at
 * build time like every REACT_APP_* key. Empty means "map not configured": the map view renders a
 * stated empty state rather than failing, and the list view is unaffected.
 */
export const MAPBOXTOKEN = (process.env.REACT_APP_MAPBOX_TOKEN ?? '').trim();
/**
 * Wallet and orchestration were hardcoded to their own ngrok hosts. They are env-driven now so the
 * production-cluster build (.env.cluster) can reach them through the api.ielect.app gateway
 * (`/wallet`, `/orchestration`); builds that set neither key keep the original hosts unchanged.
 */
export const WALLETAPI = (process.env.REACT_APP_BASE_WALLET || 'https://wallet-ielect.ngrok.app').replace(/\/+$/, '');
export const ORCHESTRATIONAPI = (process.env.REACT_APP_BASE_ORCHESTRATION || 'https://ielect-orchestration.ngrok.app').replace(
	/\/+$/,
	''
);

export const PAGE_SIZE = 20;
export const PAGE_SIZE_OPTIONS = [20, 50, 100] as const;

export interface IStateData {
	states?: IStates;
}

export const resultTypeEnum = {
	EC8A: 'Statement of Result at the Polling Unit',
	EC8B: 'Summary of Results at the RA/Ward Collation Level',
	EC8C: 'Summary of Results at the Local Government Area (LGA) Collation Level',
	EC8D: 'Summary of Results at the State Collation Level',
	EC8E: 'Summary of Results at the National Collation Level',
	EC4OH: 'List of Accredited Voters',
	EC4OG: 'Ballot Paper Accounting and Verification Statement',
	EC4OA: 'Polling Unit Accounting Form',
	EC4OB: 'Polling Unit Material Receipt Form',
	EC4OC: 'Polling Unit Staff Oath/Affirmation of Neutrality Form',
} as const;

export type ResultType = keyof typeof resultTypeEnum;

export const RESULT_TYPE_SCOPE: Partial<
	Record<
		ResultType,
		{
			label: string;
			slug: string;
			scopeKey: 'PollingUnit' | 'Ward' | 'LocalGovernment' | 'State';
			apiValue: ResultType;
		}
	>
> = {
	EC8A: {
		label: 'Polling Unit',
		slug: 'polling-unit',
		scopeKey: 'PollingUnit',
		apiValue: 'EC8A',
	},
	EC8B: {
		label: 'Ward',
		slug: 'ward',
		scopeKey: 'Ward',
		apiValue: 'EC8B',
	},
	EC8C: {
		label: 'Local Government',
		slug: 'local-government',
		scopeKey: 'LocalGovernment',
		apiValue: 'EC8C',
	},
	EC8D: {
		label: 'State',
		slug: 'state',
		scopeKey: 'State',
		apiValue: 'EC8D',
	},
};

export const getResultScopeMeta = (resultType: ResultType) => RESULT_TYPE_SCOPE[resultType];

export const getResultScopeLabel = (resultType: ResultType) => RESULT_TYPE_SCOPE[resultType]?.label ?? '';

export const getResultScopeSlug = (resultType: ResultType) => RESULT_TYPE_SCOPE[resultType]?.slug ?? '';

export type aliasType = 'Presidency' | 'Senate' | 'FHA' | 'Governor' | 'HOA' | 'Chairman' | 'Councillor';

export type typeKey = '' | 'Elections' | 'Pooling Units' | 'Wards' | 'Local Governments' | 'States' | 'Constituency';

export type electoralDivisionType = 'election' | 'constituency' | 'state' | 'lga' | 'ward' | 'pollingunit' | null;

export function getTitle(type: electoralDivisionType): string {
	switch (type) {
		case 'constituency':
			return 'Constituency';
		case 'state':
			return 'State';
		case 'lga':
			return 'Local Government';
		case 'ward':
			return 'Ward';
		case 'pollingunit':
			return 'Polling Unit';
		default:
			return '';
	}
}

/**
 * SignalR hub URLs. A const object rather than an enum because string enum members must be
 * literals, and the hubs that have a gateway route now follow the build's env: core's hub via
 * COREAPI, VESR's via REACT_APP_SIGNALR_VESR, coupons via WALLETAPI. Each falls back to the value
 * it had when it was hardcoded, so a build without those keys produces the same URLs as before.
 * DEFAULT and BROADCAST have no production route and stay on their ngrok hosts.
 */
export const SIGNALRAPIS = {
	DEFAULT: 'https://erms-core.ngrok.app/notificationHub',
	BROADCAST: 'https://erms-broadcast.ngrok.app/notificationHub',
	AGENTSTATS: `${COREAPI || 'https://ielect-api.ngrok.app/core'}/notificationHub`,
	LOCATION: `${COREAPI || 'https://ielect-api.ngrok.app/core'}/notificationHub`,
	VESR: process.env.REACT_APP_SIGNALR_VESR || 'https://ielect-vesr.ngrok.app/hubs/vesr',
	COUPONS: `${WALLETAPI}/hubs/coupons`,
} as const;
export type SIGNALRAPIS = (typeof SIGNALRAPIS)[keyof typeof SIGNALRAPIS];

export interface IOrganization {
	imgSrc: string;
	name: string;
	founded: string;
}

export const partyData: IOrganization = {
	imgSrc: partylogo,
	name: 'All Progressive Congress',
	founded: '1999',
};

export const superAdminData: IOrganization = {
	imgSrc: companylogo,
	name: 'I-Elect',
	founded: '2021',
};

const role = getUserData().user?.role;

const userRole = !role || !Array.isArray(role) ? [role ?? ''] : role;

export const APPUSERROLES = {
	SUPERADMIN: 'Super Admin',
	PARTYADMIN: 'Party Admin',
	CANDIDATE: 'Candidate',
	PARTYAGENTA: 'Party Agent - EC8A',
	PARTYAGENTB: 'Party Agent - EC8B',
	PARTYAGENTC: 'Party Agent - EC8C',
	PARTYAGENTD: 'Party Agent - EC8D',
	OBSERVERADMIN: 'Observer Admin',
};

export const ISSUPERADMIN = userRole.includes(APPUSERROLES.SUPERADMIN);

export const ISOBSERVERADMIN = userRole.includes(APPUSERROLES.OBSERVERADMIN);

export interface IPARTY_STORE_PROPS {
	partyId: string | number;
	longName: string;
	shortName: string;
}

const getPartyFromLocalState = (): IPARTY_STORE_PROPS => {
	const fallback = {
		partyId: 0,
		longName: 'I-ELECT',
		shortName: 'I-ELECT',
	};

	const data = localStorage.getItem('party');
	if (!data) return fallback;

	try {
		return JSON.parse(data) as IPARTY_STORE_PROPS;
	} catch {
		return fallback;
	}
};

const localStateParty = getPartyFromLocalState();

const ORGANIZATION: IGetPartyByIDState = {
	id: Number(localStateParty.partyId),
	shortName: localStateParty.shortName,
	longName: localStateParty.longName,
	description: '',
	phone: '',
	address: '',
	city: '',
	state: '',
	email: '',
	followerCount: 0,
	followingCount: 0,
	viewOrder: 0,
	likesCount: 0,
	slogan: '',
	logo: companylogo,
	founded: '',
	isActive: false,
};

export interface IORGANIZATION_SELECTED_ELECTION {
	electionName: string;
	electionId: string | number;
	stateId?: number;
	electIonType?: ELECTIONTYPE;
}

export const DEFAULT_ORGANIZATION_SELECTED_ELECTION: IORGANIZATION_SELECTED_ELECTION = {
	electionId: '',
	electionName: '',
	stateId: 0,
};
export interface IORGANIZATION extends IGetPartyByIDState {
	modalProps: IUseAppModal;
	selectedElection: IORGANIZATION_SELECTED_ELECTION;
	storeElectionSelection: (election: IInputSearchResult, stateId?: number) => void;
	storePartySelection: (party: IInputSearchResult) => void;
	onSelectElection: () => void;
	cancelSelection: () => void;
	isCandidate: boolean;
	isObserver: boolean;
}

export const DEFAULT_ORGANIZATION: IGetPartyByIDState = {
	...ORGANIZATION,
	logo: companylogo,
	founded: '2025',
};

export const COMPANY_ORGANIZATION: IGetPartyByIDState = {
	...ORGANIZATION,
	founded: '2025',
	isActive: true,
};

export type ELECTIONTYPE =
	| 'President'
	| 'Senate'
	| 'Federal House Of Representatives'
	| 'State Governorship'
	| 'State House of Representative'
	| 'Local Government Chairmanship'
	| 'Local Government Councillorship';

const getElectionSelected = () => {
	const data = localStorage.getItem('election');
	if (!data) return;
	const election = JSON.parse(data) as { label: string; id: string | number };

	return election;
};

export const SELECTED_ELECTION = getElectionSelected();

export enum ResultSource {
	Citizen = 1,
	Agent = 2,
	IRev = 3,
	AI = 4,
}

/** Maps each result source to the result types it supports. */
export const RESULT_SOURCE_CAPABILITIES: Record<ResultSource, ResultType[]> = {
	[ResultSource.Citizen]: ['EC8A'],
	[ResultSource.Agent]: ['EC8A', 'EC8B', 'EC8C', 'EC8D'],
	[ResultSource.IRev]: ['EC8A'],
	[ResultSource.AI]: ['EC8A', 'EC8B', 'EC8C', 'EC8D'],
};

/** Returns true when source only supports polling-unit-level results (EC8A). */
export const isSourcePollingUnitOnly = (source: number | string | null | undefined): boolean => {
	const key = Number(source);
	const caps = RESULT_SOURCE_CAPABILITIES[key as ResultSource];
	return !!caps && caps.length === 1 && caps[0] === 'EC8A';
};

/** Returns the list of result types the given source supports. */
export const getResultTypesForSource = (source: number | string | null | undefined): ResultType[] => {
	const key = Number(source);
	return RESULT_SOURCE_CAPABILITIES[key as ResultSource] ?? [];
};

/** Returns true when the given source supports the given result type. */
export const isResultTypeAvailableForSource = (source: number | string | null | undefined, resultType: ResultType): boolean =>
	getResultTypesForSource(source).includes(resultType);

// Canonical agent / volunteer status catalogue — one source of truth for IDs, labels, and visual tones.
// id=1 is the workflow entry point (Application Received) and id=8 is the terminal success state (Confirmed Agent).
// Note: id=2 ('approved') has been retired from the backend workflow and is intentionally omitted here.
// Do not reuse id=2 — the gap is deliberate so historical/external references stay unambiguous.
export const VolunteerStatus = {
	'pending': 1,
	'rejected': 3,
	'pending acceptance': 4,
	'offer expired': 5,
	'pending review': 6,
	'reserved': 7,
	'confirmed': 8,
} as const;

export const VolunteerStatusText: Record<number, string> = {
	1: 'Application Received',
	3: 'Rejected',
	4: 'Offer Sent',
	5: 'Offer Expired',
	6: 'Pending',
	7: 'Reserved',
	8: 'Confirmed Agent',
};

export type VolunteerStatusTone = 'success' | 'warning' | 'danger' | 'info' | 'muted' | 'primary';

export const VolunteerStatusToneMap: Record<number, VolunteerStatusTone> = {
	1: 'muted',
	3: 'danger',
	4: 'warning',
	5: 'danger',
	6: 'muted',
	7: 'primary',
	8: 'success',
};

export const VolunteerStatusBootstrapBg: Record<number, string> = {
	1: 'secondary',
	3: 'danger',
	4: 'warning',
	5: 'danger',
	6: 'secondary',
	7: 'primary',
	8: 'success',
};

export const VolunteerStatusDotClass: Record<number, string> = {
	1: 'received',
	3: 'rejected',
	4: 'offer',
	5: 'expired',
	6: 'pending-review',
	7: 'reserved',
	8: 'confirmed',
};

export const getVolunteerStatusLabel = (id: number | null | undefined): string =>
	id != null && VolunteerStatusText[id] ? VolunteerStatusText[id] : 'Unknown Status';

/**
 * Filter/display order for the status catalogue — live workflow first, then terminal states,
 * so admins read the funnel top-down. Every catalogue id must appear here (pinned by test);
 * a status missing from this list becomes unreachable in every status filter that builds from it.
 */
export const VOLUNTEER_STATUS_ORDER = [1, 4, 8, 7, 6, 5, 3] as const;

/**
 * Only "Application Received" can be approved.
 *
 * Mirrors the backend gate in api.core `VolunteerRepository.ApproveRequest`, which throws
 * "Volunteer {id} is not in 'Application Received' status and cannot be approved" for anything
 * else. Do NOT substitute `!isApproved` here — `IsApproved` is a sticky "was ever offered" audit
 * flag that is never reset (see the backend's BackfillVolunteerIsApproved migration note), so it
 * both hides live offers and wrongly admits Rejected/Pending/Reserved applicants.
 *
 * Keep this in step with the backend gate if approval preconditions ever relax.
 */
export const isAssignableVolunteerStatus = (id: number | null | undefined): boolean => id === VolunteerStatus.pending;

/**
 * Why an applicant cannot be assigned, phrased for the admin reading the row rather than
 * describing the API. Surfaced as the Assign menu item's `description`.
 * Returns an empty string for the assignable status so callers can treat it as "no reason".
 */
export const getVolunteerAssignBlockedReason = (id: number | null | undefined): string => {
	switch (id) {
		case VolunteerStatus.pending:
			return '';
		case VolunteerStatus.rejected:
			return 'Application was rejected.';
		case VolunteerStatus['pending acceptance']:
			return 'Offer already sent — waiting for them to accept or for it to expire.';
		case VolunteerStatus['offer expired']:
			return 'Their offer expired; the reserve applicant is promoted automatically.';
		case VolunteerStatus['pending review']:
			return 'On the waiting list behind the current offer holder.';
		case VolunteerStatus.reserved:
			return 'Held as reserve — promoted automatically if the current offer expires.';
		case VolunteerStatus.confirmed:
			return 'Already the confirmed agent for this division.';
		default:
			return `Only applicants in "${VolunteerStatusText[VolunteerStatus.pending]}" can be assigned (currently ${getVolunteerStatusLabel(id)}).`;
	}
};
